import React, { useEffect, useState, useCallback, useMemo } from "react";
import api from "../../lib/api";
import { formatINR, categoryMeta, daysLabel, formatShortDate } from "../../lib/format";
import { Card } from "../../components/ui/card";
import { Skeleton } from "../../components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { CalendarClock } from "lucide-react";

function Row({ c }) {
  const meta = categoryMeta(c.category); const Icon = meta.icon;
  return (
    <div className="flex items-center gap-3 py-3" data-testid={`timeline-row-${c.id}`}>
      <div className="h-10 w-10 rounded-xl bg-secondary flex items-center justify-center shrink-0"><Icon size={17} strokeWidth={1.5} className="text-navy" /></div>
      <div className="min-w-0 flex-1">
        <p className="font-medium text-ink truncate">{c.name}</p>
        <p className="text-xs text-muted-foreground">{c.next_due_date ? `${formatShortDate(c.next_due_date)} · ${daysLabel(c.days_remaining)}` : meta.label}</p>
      </div>
      <p className="font-semibold text-ink tnum">{formatINR(c.amount)}</p>
    </div>
  );
}

function List({ items, empty }) {
  if (!items || items.length === 0) return <p className="text-sm text-muted-foreground py-10 text-center">{empty}</p>;
  return <div className="divide-y divide-border/60">{items.map((c) => <Row key={c.id} c={c} />)}</div>;
}

function MonthCalendar({ commitments }) {
  const today = new Date();
  const year = today.getFullYear(); const month = today.getMonth();
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const byDay = useMemo(() => {
    const m = {};
    commitments.forEach((c) => {
      if (!c.next_due_date) return;
      const d = new Date(c.next_due_date);
      if (d.getFullYear() === year && d.getMonth() === month) {
        const day = d.getDate();
        m[day] = (m[day] || 0) + 1;
      }
    });
    return m;
  }, [commitments, year, month]);

  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  return (
    <div>
      <p className="text-sm font-medium text-ink mb-3">{today.toLocaleDateString("en-IN", { month: "long", year: "numeric" })}</p>
      <div className="grid grid-cols-7 gap-1 text-center">
        {["S", "M", "T", "W", "T", "F", "S"].map((d, i) => <div key={i} className="text-[11px] text-muted-foreground py-1">{d}</div>)}
        {cells.map((d, i) => (
          <div key={i} className={`aspect-square rounded-lg flex flex-col items-center justify-center text-sm ${d === today.getDate() ? "bg-navy text-white" : d ? "bg-secondary/50 text-ink" : ""}`}>
            {d && <span className="tnum">{d}</span>}
            {d && byDay[d] && <span className={`h-1.5 w-1.5 rounded-full mt-0.5 ${d === today.getDate() ? "bg-mint" : "bg-mint"}`} />}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Timeline() {
  const [data, setData] = useState(null);
  const [all, setAll] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [t, c] = await Promise.all([api.get("/timeline"), api.get("/commitments")]);
      setData(t.data); setAll(c.data);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="space-y-3"><Skeleton className="h-10 w-full rounded-full" />{[1,2,3].map(i => <Skeleton key={i} className="h-16 rounded-2xl" />)}</div>;

  return (
    <div>
      <div className="flex items-center gap-2 mb-4">
        <CalendarClock size={22} className="text-navy" strokeWidth={1.5} />
        <h1 className="font-heading text-2xl font-medium text-ink">Timeline</h1>
      </div>
      <Tabs defaultValue="upcoming">
        <TabsList className="rounded-full bg-secondary flex w-full overflow-x-auto no-scrollbar">
          <TabsTrigger value="upcoming" className="rounded-full flex-1" data-testid="tl-upcoming">Upcoming</TabsTrigger>
          <TabsTrigger value="calendar" className="rounded-full flex-1" data-testid="tl-calendar">Calendar</TabsTrigger>
          <TabsTrigger value="overdue" className="rounded-full flex-1" data-testid="tl-overdue">Overdue</TabsTrigger>
          <TabsTrigger value="cancelled" className="rounded-full flex-1" data-testid="tl-cancelled">Cancelled</TabsTrigger>
        </TabsList>
        <Card className="rounded-2xl border-border shadow-card-sm p-5 mt-4">
          <TabsContent value="upcoming"><List items={data.upcoming} empty="No upcoming commitments." /></TabsContent>
          <TabsContent value="calendar"><MonthCalendar commitments={all} /></TabsContent>
          <TabsContent value="overdue"><List items={data.overdue} empty="Nothing overdue — all clear." /></TabsContent>
          <TabsContent value="cancelled">
            <List items={[...(data.paused || []), ...(data.cancelled || [])]} empty="No paused or cancelled commitments." />
          </TabsContent>
        </Card>
      </Tabs>
      <p className="text-xs text-muted-foreground mt-4 px-1">Amounts shown are the actual upcoming deduction, not monthly equivalents.</p>
    </div>
  );
}
